/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Grid,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Select,
  MenuItem,
  Input,
  Tooltip,
  InputLabel,
} from '@material-ui/core'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { useParams, navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { includeDDI } from '_/views/new-service/utils/includeDDI'
import { Loader } from '@refera/ui-web'

import { getClassification } from '_/modules/classification/actions'
import { classificationsSelector } from '_modules/classification/selectors'

import Loading from '_/components/loading'
import { camelize } from '_utils/token'
import { addAgencyStaffUser, getAgency, patchAgencyStaffUser } from '_/modules/agency/actions'
import { agencySelector } from '_modules/agency/selectors'

import { AddCity } from './add-city'
import { BasicInput, MaskBasicInput } from '_/components/inputs'
import useStyles from './styles'
import NavbarButtons from './navbar-buttons'
import { AddGroupPermissions } from './add-group-permission'
import { getProfilePermissionGroup } from '_/modules/profile/actions'
import { getPermissionsGroupSelector } from '_/modules/profile/selectors'
import { EMAIL_REGEX, PHONE_REGEX } from '_/utils/constants'
import { Checkbox } from '@mui/material'
import HeaderTitle from '_/components/header-title'
import { getAgencyStaffUsers } from '_/services/agency'

const AgencyUser = () => {
  const styles = useStyles()
  const dispatch = useDispatch()

  const { agencyId, userId } = useParams()
  const { showToast } = useToast()

  const agency = useSelector(agencySelector)
  const classifications = useSelector(classificationsSelector)

  const groupsPermission = useSelector(getPermissionsGroupSelector)
  const [user, setUser] = useState({})
  const [pageLoading, setPageLoading] = useState(true)
  const [requestLoading, setRequestLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(!userId)
  const [permissionsGroup, setPermissionsGroup] = useState([])

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      name: '',
      cities: user?.cities || [],
      newHelpRequestsNotification: 'email',
    },
    shouldUnregister: false,
  })

  const { handleSubmit, control, setValue, watch } = methods
  const isAdminObserver = watch('isAdmin')

  const menuProps = useMemo(
    () => ({
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
    }),
    []
  )

  const handleNavigate = () => {
    navigate(`/imobiliaria/${agencyId}`)
  }

  const handleSave = useCallback(
    async dataObject => {
      // eslint-disable-next-line no-unused-vars
      const { addGroupPermission, newHelpRequestsNotification, ...data } = dataObject

      setRequestLoading(true)
      if (user?.email === data.email) {
        delete data.email
      }

      try {
        const newRequestsForHelpMapping = {
          none: 'not_being_communicated',
          email: 'communicated_by_email',
          whatsapp: 'communicated_by_whats',
        }

        const payload = {
          permissionsGroup: permissionsGroup.map(permission => permission.id),
          ...data,
          new_requests_for_help: newRequestsForHelpMapping[newHelpRequestsNotification || 'email'],
        }

        if (!userId) {
          await dispatch(addAgencyStaffUser(payload))
            .then(() => {
              showToast({
                message: 'Usuário salvo com sucesso.',
                type: 'success',
              })
              handleNavigate()
            })
            .catch(err => {
              if (err.error_message) {
                return showToast({
                  message: err.error_message,
                  type: 'error',
                })
              }

              if (err.response) {
                return showToast({
                  message: err.response.data,
                  type: 'error',
                })
              }

              if (err.detail) {
                return showToast({
                  message: err.detail,
                  type: 'error',
                })
              }

              return showToast({
                message: 'Ocorreu um erro ao salvar o usuário.',
                type: 'error',
              })
            })
            .finally(() => {
              setRequestLoading(false)
            })
        } else {
          await dispatch(patchAgencyStaffUser(userId, payload))
            .then(() => {
              showToast({
                message: 'Usuário salvo com sucesso.',
                type: 'success',
              })
              handleNavigate()
            })
            .catch(err => {
              if (err.error_message) {
                return showToast({
                  message: err.error_message,
                  type: 'error',
                })
              }

              if (err.response) {
                return showToast({
                  message: err.response.data,
                  type: 'error',
                })
              }

              if (err.detail) {
                return showToast({
                  message: err.detail,
                  type: 'error',
                })
              }

              return showToast({
                message: 'Ocorreu um erro ao salvar o usuário.',
                type: 'error',
              })
            })
            .finally(() => {
              setRequestLoading(false)
            })
        }
      } catch (err) {
        if (err.response) {
          showToast({
            message: err.response.data,
            type: 'error',
          })
          setRequestLoading(false)
        }
      }

      return setRequestLoading(false)
    },
    [permissionsGroup, showToast, dispatch]
  )

  useEffect(() => {
    getAgencyStaffUsers(agencyId, userId || undefined)
      .then(data => setUser(camelize(data)))
      .then(() => setPageLoading(false))
  }, [agencyId, userId]) // FETCHES USER

  useEffect(() => {
    if (agencyId) {
      dispatch(getAgency(agencyId))
    }
  }, [agencyId]) // FETCHES AGENCY

  useEffect(() => {
    if (user?.permissionsGroup) {
      setPermissionsGroup(user.permissionsGroup)
    }
    if (user?.getRoles) {
      setValue('role', user.getRoles)
    }
    const helpRequestsMapToUI = {
      not_being_communicated: 'none',
      communicated_by_email: 'email',
      communicated_by_whats: 'whatsapp',
    }

    if (user?.newRequestsForHelp) {
      setValue(
        'newHelpRequestsNotification',
        helpRequestsMapToUI[user.newRequestsForHelp] || 'email'
      )
    } else if (user?.id && !user?.newHelpRequestsNotification) {
      setValue('newHelpRequestsNotification', 'email')
    }
    if (userId && user?.id) {
      setValue('isActive', user.isActive ?? user.is_active ?? true)
    }
  }, [user, userId, setValue])

  useEffect(() => {
    dispatch(
      getClassification({
        pageSize: 1000,
        active: 'True',
        agency: agencyId,
        classificationType: 'auto_responsible_selection',
      })
    )
  }, [])

  const getPermissionsGroup = useCallback(() => {
    if (!groupsPermission?.results || groupsPermission?.results?.length === 0) {
      setPageLoading(true)

      return dispatch(
        getProfilePermissionGroup({
          pageSize: 1000,
          agency: agencyId,
        })
      )
        .then(() => {
          return setPageLoading(false)
        })
        .catch(() => {
          showToast({
            type: 'error',
            message: 'Ocorreu um erro ao buscar os registros dos grupos de permissões.',
          })
          return setPageLoading(false)
        })
    }

    return null
  }, [dispatch])

  // On page first load
  useEffect(() => {
    getPermissionsGroup()
  }, [])

  if ((pageLoading && userId) || (!user?.id && userId)) {
    return <Loading />
  }

  return (
    <>
      <Grid className={styles.main}>
        <FormProvider {...methods}>
          <HeaderTitle
            title={`Cadastro intermediária / ${agency?.toJS().name}`}
            backButtonAction={handleNavigate}
          >
            <NavbarButtons
              isEditing={isEditing}
              isLoading={requestLoading}
              setIsEditing={setIsEditing}
              user={user}
              hasChanges
            />
          </HeaderTitle>
          <form id="user-form" onSubmit={handleSubmit(handleSave)} className={styles.container}>
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados principais</InputLabel>
              <div className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label="Nome completo"
                    name="name"
                    labelClasses={styles.inputLabel}
                    required
                    disabled={!isEditing}
                    defaultValue={user?.name || ''}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <BasicInput
                    label="E-mail"
                    name="email"
                    required
                    labelClasses={styles.inputLabel}
                    disabled={!isEditing}
                    defaultValue={user?.email || ''}
                    rules={{
                      pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
                      required: { value: true, message: 'Esse campo é obrigatório' },
                    }}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <MaskBasicInput
                    label="Telefone"
                    name="phone"
                    mask="+55 (99) 9 9999-9999"
                    required
                    labelClasses={styles.inputLabel}
                    disabled={!isEditing}
                    rules={{
                      required: { value: true, message: 'Esse campo é obrigatório' },
                      pattern: { value: PHONE_REGEX, message: 'Telefone inválido' },
                    }}
                    defaultValue={user?.phone ? includeDDI(user?.phone) : ''}
                  />
                </Grid>

                <div className={styles.checkboxField}>
                  <Grid className={styles.input}>
                    <Controller
                      name="isActive"
                      control={control}
                      defaultValue={user?.isActive ?? user?.is_active ?? true}
                      disabled={!isEditing}
                      render={({ value, onChange }) => {
                        return (
                          <FormControlLabel
                            label="Ativo"
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={Boolean(value)}
                                disabled={!isEditing}
                                onChange={e => onChange(e.target.checked)}
                              />
                            }
                          />
                        )
                      }}
                    />
                  </Grid>
                </div>
              </div>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Outras informações</InputLabel>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <Controller
                    name="newHelpRequestsNotification"
                    control={control}
                    defaultValue={
                      user?.newRequestsForHelp
                        ? {
                            not_being_communicated: 'none',
                            communicated_by_email: 'email',
                            communicated_by_whats: 'whatsapp',
                          }[user?.newRequestsForHelp] || 'email'
                        : user?.newHelpRequestsNotification || 'email'
                    }
                    render={({ value, onChange }) => (
                      <FormControl style={{ width: '100%' }}>
                        <FormLabel className={styles.inputLabel}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>Novos Pedidos de Ajuda</span>
                            <Tooltip
                              arrow
                              disableFocusListener
                              disableTouchListener
                              title={
                                <Typography className={styles.tooltipText}>
                                  Informa se o usuário quer ser comunicado, e como, sobre a criação
                                  de novos pedidos de ajuda em chamados nos quais é responsável.
                                </Typography>
                              }
                              placement="bottom-end"
                            >
                              <HelpOutlineOutlinedIcon
                                className={styles.tooltipIcon}
                                style={{ marginLeft: 4 }}
                              />
                            </Tooltip>
                          </div>
                        </FormLabel>
                        <Select
                          disabled={!isEditing}
                          labelId="new-help-requests"
                          value={value}
                          onChange={e => onChange(e.target.value)}
                          input={<Input />}
                          MenuProps={menuProps}
                          className={styles.select}
                        >
                          <MenuItem value="none">(Não ser comunicado)</MenuItem>
                          <MenuItem value="email">Comunicado por email</MenuItem>
                          {/* Opção de WhatsApp removida conforme solicitado na task PREF-15185
                          {agency?.contractType !== 'SAAS' && (
                            <MenuItem value="whatsapp">Comunicado por whatsapp</MenuItem>
                          )}
                          */}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid className={styles.input}>
                  <Controller
                    name="classification"
                    control={control}
                    defaultValue={user?.classification || []}
                    render={({ value, onChange }) => (
                      <FormControl style={{ width: '100%' }}>
                        <FormLabel shrink id="classification" className={styles.inputLabel}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>Classificação</span>
                            <Tooltip
                              arrow
                              disableFocusListener
                              disableTouchListener
                              title={
                                <Typography className={styles.tooltipText}>
                                  Campo que facilita a distribuição de chamados pelos usuários da
                                  empresa.
                                  <br />
                                  Se estiver sem conteúdo, é porque sua empresa não tem tal
                                  funcionalidade.
                                </Typography>
                              }
                              placement="bottom-end"
                            >
                              <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                            </Tooltip>
                          </div>
                        </FormLabel>
                        <Select
                          multiple
                          disabled={!isEditing}
                          labelId="classification"
                          id="multiple-classifications"
                          aria-labelledby="classification"
                          onChange={e => onChange(e.target.value)}
                          value={value}
                          input={<Input label="Classificação" />}
                          MenuProps={menuProps}
                          className={styles.select}
                        >
                          {classifications.map(item => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.classificationName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid className={styles.input}>
                  <BasicInput
                    label={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          transform: 'translateY(-8px)',
                        }}
                      >
                        <span>Código externo</span>
                        <Tooltip
                          arrow
                          disableFocusListener
                          disableTouchListener
                          title={
                            <Typography className={styles.tooltipText}>
                              Campo usado em caso de integração com outros sistemas, onde é possível
                              inserir o código deste usuário no sistema integrado.
                            </Typography>
                          }
                          placement="bottom-end"
                        >
                          <HelpOutlineOutlinedIcon
                            className={styles.tooltipIcon}
                            style={{ marginLeft: 4, lineHeight: '1.6rem' }}
                          />
                        </Tooltip>
                      </div>
                    }
                    name="externalCode"
                    containerClasses={styles.codeExternField}
                    labelClasses={styles.inputLabel}
                    disabled={!isEditing}
                    defaultValue={user?.externalCode || ''}
                  />
                </Grid>

                <div className={styles.checkboxField}>
                  <Grid className={styles.input}>
                    <Controller
                      name="participateInDistribution"
                      control={control}
                      defaultValue={user?.participateInDistribution}
                      disabled={!isEditing}
                      render={({ value, onChange }) => {
                        return (
                          <FormControlLabel
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={value}
                                disabled={!isEditing}
                                defaultChecked={user?.participateInDistribution}
                                onChange={e => onChange(e.target.checked)}
                              />
                            }
                            label={
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>Participa da distribuição</span>
                                <Tooltip
                                  arrow
                                  disableFocusListener
                                  disableTouchListener
                                  title={
                                    <Typography className={styles.tooltipText}>
                                      Se marcar que o usuário participa da distribuição, o usuário
                                      fica disponível no processo automático de distribuição de
                                      chamados e pode ficar como responsável por chamados, assim que
                                      são criados. Desde que também esteja Ativo.
                                    </Typography>
                                  }
                                  placement="bottom-end"
                                >
                                  <HelpOutlineOutlinedIcon
                                    className={styles.tooltipIcon}
                                    style={{ marginLeft: 4 }}
                                  />
                                </Tooltip>
                              </div>
                            }
                          />
                        )
                      }}
                    />
                  </Grid>
                </div>
                <div className={styles.checkboxField}>
                  <Grid className={styles.input}>
                    <Controller
                      name="accountManager"
                      control={control}
                      defaultValue={user?.accountManager}
                      disabled={!isEditing}
                      render={({ value, onChange }) => {
                        return (
                          <FormControlLabel
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={value}
                                disabled={!isEditing}
                                defaultChecked={user?.participateInDistribution}
                                onChange={e => onChange(e.target.checked)}
                              />
                            }
                            label={
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>Gerente de Carteira</span>
                                <Tooltip
                                  arrow
                                  disableFocusListener
                                  disableTouchListener
                                  title={
                                    <Typography className={styles.tooltipText}>
                                      Se marcar que o usuário é gerente de carteira, o usuário
                                      ficará disponível no processo de gestão de chamados de
                                      clientes, desde que esteja ativo.
                                    </Typography>
                                  }
                                  placement="bottom-end"
                                >
                                  <HelpOutlineOutlinedIcon
                                    className={styles.tooltipIcon}
                                    style={{ marginLeft: 4 }}
                                  />
                                </Tooltip>
                              </div>
                            }
                          />
                        )
                      }}
                    />
                  </Grid>
                </div>
              </Grid>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Permissões</InputLabel>
              <Grid className={styles.wrapper}>
                <div className={styles.adminField}>
                  <Grid className={styles.input}>
                    <Controller
                      name="isAdmin"
                      control={control}
                      defaultValue={user?.isAdmin}
                      disabled={!isEditing}
                      render={({ value, onChange }) => {
                        return (
                          <FormControlLabel
                            label="Admin"
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={value}
                                disabled={!isEditing}
                                defaultChecked={user?.isAdmin}
                                onChange={e => onChange(e.target.checked)}
                              />
                            }
                          />
                        )
                      }}
                    />
                    {!isAdminObserver && (
                      <AddGroupPermissions
                        permissionsGroup={permissionsGroup}
                        setPermissionsGroup={setPermissionsGroup}
                        isEditing={isEditing}
                      />
                    )}
                  </Grid>
                </div>
              </Grid>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Regiões de atendimento</InputLabel>
              <Grid className={styles.wrapperFull}>
                <AddCity isProvider user={user} staffUsers={user?.cities} isEditing={isEditing} />
              </Grid>
            </div>

            {requestLoading && <Loader hasBackdrop open={requestLoading} label="Aguarde..." />}
          </form>
        </FormProvider>
      </Grid>
    </>
  )
}

export default AgencyUser
